// FT-200 STEP-05 (REQ-08, REQ-09): чистые функции показа кабинета клиента — суммы, залог, документы, история,
// тексты отказов оплаты. Без Vue и без стора: проверяются спеками блоков через то, что видит клиент.
//
// Деньги нового ответа кабинета: начисления бронирования — числа, суммы платежей провайдеров и to_pay — строки
// десятичной записи ("3000.0"), суммы Т-Банка — рубли строкой (не копейки). Сумму к оплате кабинет не считает:
// она приходит с сервера (Fleet::BookingMoney.to_pay — та же формула, что в карточке сотрудника).
import { isTransientNetworkError } from "@/utils/networkErrors";
import moment from "moment";
import { momentLocaleFor } from "@/utils/localeInfra";
import { moneyCents, parseAmount, roundMoney, CARD_CHECK_WINDOW_MS } from "@/utils/clientCabinetCore";

// Деньги, отчёт о сбое и окно проверки привязки — в clientCabinetCore («набор возврата» не зависит от этого модуля);
// здесь — реэкспорт для блоков.
export {
  moneyCents, parseAmount, roundMoney, formatAmount, money, redactedRequestError, reportServerError,
  CARD_CHECK_WINDOW_MS,
} from "@/utils/clientCabinetCore";

// REQ-13: цвета уведомлений и кнопки подтверждения кабинета. $notify — v-snackbar с белым текстом на этом цвете,
// $confirm — текстовая кнопка этого цвета на белом. Штатные error / success / warning Vuetify (#ff5252, #4caf50,
// #fb8c00) дают 3,2 / 2,8 / 2,4:1, эти — 5,6 / 5,1 / 5,2:1.
export const NOTIFY_COLORS = Object.freeze({ error: "#c62828", success: "#2e7d32", warning: "#a15c00" });

// Начисления бронирования — Fleet::BookingMoney::CHARGE_COLUMNS в api, тот же состав и порядок.
export const CHARGE_FIELDS = [
  "rental_cost", "delivery", "delivery_end", "equipment", "addons_cost", "insurance", "clean_payment",
  "add_drivers_cost", "hours_cost", "mileage_cost", "damage", "gas", "fine", "other", "other_end", "hours_cost_end",
];

const num = (value) => {
  const amount = parseAmount(value);
  return Number.isNaN(amount) ? 0 : amount;
};

// Периодическое бронирование живёт на балансе клиента: отрицательное значение — долг, положительное — запас.
export function periodicDebt(balance) {
  const amount = parseAmount(balance);
  return !Number.isNaN(amount) && amount < 0 ? roundMoney(Math.abs(amount)) : 0;
}

// Один источник суммы долга для блока «Сейчас» и финансовой сводки. У периодического отрицательный баланс клиента
// точнее общего to_pay бронирования; если долга на балансе нет, сохраняем серверный to_pay как раньше.
export function cabinetDebtAmount(booking, toPay, balance) {
  const balanceDebt = booking && booking.payments_type === "periodic" ? periodicDebt(balance) : 0;
  if (balanceDebt > 0) return balanceDebt;
  const due = parseAmount(toPay);
  return Number.isNaN(due) ? NaN : roundMoney(Math.max(0, due));
}

// Дополнительная CTA периодического: погасить текущий долг и заранее внести ближайшее списание.
export function combinedPaymentAmount(debt, nextChargeAmount) {
  const current = parseAmount(debt);
  const upcoming = parseAmount(nextChargeAmount);
  const currentCents = !Number.isNaN(current) && current > 0 ? moneyCents(debt) : 0;
  const upcomingCents = !Number.isNaN(upcoming) && upcoming > 0 ? moneyCents(nextChargeAmount) : 0;
  return (currentCents + upcomingCents) / 100;
}

export function formatCabinetDate(value, locale, pattern = "D MMMM, HH:mm") {
  if (!value) return "";
  const wall = moment.utc(value, "DD-MM-YYYY HH:mm", true);
  const date = wall.isValid() ? wall : moment(value);
  return date.isValid() ? date.locale(momentLocaleFor(locale || "ru")).format(pattern) : String(value);
}

// Залог по единице аренды — Fleet::BookingMoney.current_deposit.
export function depositField(booking) {
  if (!booking) return "deposit";
  if (booking.hourly) return "hourly_deposit";
  if (booking.weekly) return "weekly_deposit";
  if (booking.monthly) return "monthly_deposit";
  return "deposit";
}

export function currentDeposit(booking) {
  return booking ? num(booking[depositField(booking)]) : 0;
}

// Начислено с залогом — Fleet::BookingMoney.cost: залог в счёте, пока машина не возвращена (ride).
export function bookingCost(booking) {
  if (!booking) return 0;
  const charges = CHARGE_FIELDS.reduce((sum, field) => {
    const cents = moneyCents(booking[field]);
    return sum + (Number.isNaN(cents) ? 0 : cents);
  }, 0);
  const deposit = booking.ride ? 0 : moneyCents(currentDeposit(booking));
  return (charges + (Number.isNaN(deposit) ? 0 : deposit)) / 100;
}

const DEPOSIT_GROUP = "Залог";

// Залог для строки в «Оплате»: сумма залога бронирования и только то, что сервер утверждает явно, — возврат.
// Возврат — проведённые платежи группы «Залог» с operation=false (так их пишет BookingCard при приёмке).
// «Внесён», «не внесён», «удержан» не выводятся: залог могли принять другой группой или ещё не вернуть по
// регламенту проката — кабинет этого не знает.
//   { amount, returned, state }: state — "returned" (возвращён не меньше внесённого), "partly_returned" или null.
export function depositInfo(booking) {
  if (!booking) return null;
  let paidInCents = 0;
  let paidOutCents = 0;
  (booking.counts || []).forEach((count) => {
    if (!count || !count.completed || count.group !== DEPOSIT_GROUP) return;
    const cents = moneyCents(count.sum);
    if (Number.isNaN(cents)) return;
    if (count.operation) paidInCents += cents;
    else paidOutCents += cents;
  });
  const configuredCents = moneyCents(currentDeposit(booking));
  const amountCents = configuredCents > 0 ? configuredCents : paidInCents;
  if (!(amountCents > 0)) return null;
  let state = null;
  if (paidOutCents > 0) state = paidInCents > 0 && paidOutCents >= paidInCents ? "returned" : "partly_returned";
  return { amount: amountCents / 100, returned: paidOutCents / 100, state };
}

// ─── Документы ──────────────────────────────────────────────────────────────

const OKIDOKI_SIGNED = ["signed", "Подписан"];
const OKIDOKI_CLOSED = ["signed", "Подписан", "rejected", "Отклонен", "cancelled", "Отменен"];
const DOCUSEAL_CLOSED = ["completed", "declined", "expired"];

// DocuSeal: клиент уже подписал, подписание не завершено и не закрыто — документ ждёт подписи проката.
export function awaitingCompany(doc) {
  return !!(doc && doc.docuseal_client_signed_at && DOCUSEAL_CLOSED.indexOf(doc.docuseal_status) === -1);
}

// Ссылка подписи для клиента, если документ ещё ждёт его подписи. DocuSeal — ссылка клиента, пока клиент не подписал
// и подписание не завершено и не отклонено; OkiDoki — ссылка подписания, пока договор не подписан, не отклонён и не
// отменён.
export function signLink(doc) {
  if (!doc) return null;
  if (doc.docuseal_client_link && !doc.docuseal_client_signed_at && DOCUSEAL_CLOSED.indexOf(doc.docuseal_status) === -1) {
    return doc.docuseal_client_link;
  }
  if (doc.okidoki_link && OKIDOKI_CLOSED.indexOf(doc.okidoki_status) === -1) return doc.okidoki_link;
  return null;
}

export function documentStatusKey(doc) {
  if (signLink(doc)) return "client_page.cabinet.documents.statuses.awaiting";
  if (awaitingCompany(doc)) return "client_page.cabinet.documents.statuses.awaiting_company";
  if (doc.docuseal_status === "completed" || OKIDOKI_SIGNED.indexOf(doc.okidoki_status) !== -1) {
    return "client_page.cabinet.documents.statuses.signed";
  }
  if (doc.docuseal_status === "declined" || ["rejected", "Отклонен"].indexOf(doc.okidoki_status) !== -1) {
    return "client_page.cabinet.documents.statuses.declined";
  }
  if (doc.docuseal_status === "expired" || ["cancelled", "Отменен"].indexOf(doc.okidoki_status) !== -1) {
    return "client_page.cabinet.documents.statuses.expired";
  }
  return "client_page.cabinet.documents.statuses.ready";
}

// ─── История платежей ───────────────────────────────────────────────────────

export const PROVIDER_STATUS_KEYS = {
  created: "client_page.cabinet.history.statuses.created",
  pending: "client_page.cabinet.history.statuses.pending",
  success: "client_page.cabinet.history.statuses.success",
  failed: "client_page.cabinet.history.statuses.failed",
  cancelled: "client_page.cabinet.history.statuses.cancelled",
  refunded: "client_page.cabinet.history.statuses.refunded",
  partially_refunded: "client_page.cabinet.history.statuses.partially_refunded",
  disputed: "client_page.cabinet.history.statuses.disputed",
  dispute_lost: "client_page.cabinet.history.statuses.dispute_lost",
};
// Статус провайдера вне списка (новый статус у провайдера) — нейтральный текст, не сырой статус.
export const PROVIDER_STATUS_UNKNOWN_KEY = "client_page.cabinet.history.statuses.unknown";

// Исход платежа Т-Банка (TinkoffPayment.outcomes); нет исхода — банк ещё не ответил.
export const TINKOFF_OUTCOME_KEYS = {
  success: "client_page.cabinet.history.outcomes.success",
  failed: "client_page.cabinet.history.outcomes.failed",
  unknown: "client_page.cabinet.history.outcomes.unknown",
  stale: "client_page.cabinet.history.outcomes.unknown",
  refund: "client_page.cabinet.history.outcomes.refund",
  partial_refund: "client_page.cabinet.history.outcomes.partial_refund",
};

export const TINKOFF_PURPOSE_KEYS = {
  card_binding: "client_page.cabinet.history.purposes.card_binding",
  booking_charge: "client_page.cabinet.history.purposes.booking_charge",
  deposit: "client_page.cabinet.history.purposes.deposit",
  balance_topup: "client_page.cabinet.history.purposes.balance_topup",
};

// Способ платежа бронирования (Count) — флаги кассы.
export function countMethodKey(count) {
  if (count.cash) return "money.cash";
  if (count.cashless) return "money.terminal";
  if (count.cash_card) return "money.card_to_card";
  if (count.entity) return "money.to_entity";
  if (count.client_balance) return "money.client_balance";
  return null;
}

// Свежая привязка карты без исхода — банк ещё проверяет карту (окно — CARD_CHECK_WINDOW_MS ядра).
export function cardBindingPending(tinkoffPayments, now = Date.now()) {
  const binding = (tinkoffPayments || [])
    .filter((p) => p && p.purpose === "card_binding")
    .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))[0];
  if (!binding) return false;
  if (["success", "failed", "refund", "partial_refund"].indexOf(binding.outcome) !== -1) return false;
  const created = Date.parse(binding.created_at);
  return !Number.isNaN(created) && now - created < CARD_CHECK_WINDOW_MS;
}

// ─── Отказы оплаты ──────────────────────────────────────────────────────────

// Текст отказа создания заказа у провайдера. Сырое сообщение сервера клиенту не показывается: публичная оплата
// отвечает 422 с текстом исключения провайдера (Stripe при сумме ниже своего минимума — по-английски, с курсом), а
// минимума Stripe ответ кабинета не знает.
export function paymentErrorKey(error) {
  const response = error && error.response;
  const status = response && response.status;
  const code = response && response.data && response.data.error;
  if (!status) {
    return isTransientNetworkError(error)
      ? "client_page.cabinet.pay_dialog.errors.network"
      : "client_page.cabinet.pay_dialog.errors.failed";
  }
  if (status === 422 && code === "return_url_not_allowed") return "client_page.cabinet.pay_dialog.errors.return_url";
  if (status === 404) return "client_page.cabinet.pay_dialog.errors.not_found";
  if (status === 403) return "client_page.cabinet.pay_dialog.errors.unavailable";
  if (status === 429) return "client_page.cabinet.pay_dialog.errors.rate_limited";
  return "client_page.cabinet.pay_dialog.errors.failed";
}
